import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Building, Eye, EyeOff, Save, Shield, Upload, Activity, AlertTriangle } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import api from '../api/axios';
import { toast } from 'sonner';
import { resolveMediaUrl } from '../lib/mediaUrl';
import { appendAdminActivityLog, getAdminActivityLogs } from '../lib/adminActivityLog';

const emptyTenantForm = {
  name: '',
  slug: '',
  ticket_prefix: '',
  adminEmail: '',
  password: '',
  adminName: '',
  support_email: '',
  logo_url: '',
  primary_color: '#4f46e5',
  accent_color: '#a855f7',
  portal_name: '',
  is_active: true,
};

const slugify = (value = '') =>
  String(value)
    .toLowerCase()
    .trim()
    .replaceAll(/[^a-z0-9]+/g, '-')
    .replaceAll(/^-+|-+$/g, '');

const suggestTicketPrefix = (name = '', slug = '') => {
  const base = String(slug || name || '')
    .toUpperCase()
    .replaceAll(/[^A-Z0-9]/g, '');
  const candidate = (base || 'TKT').slice(0, 3);
  return candidate.padEnd(3, 'X');
};

const sanitizeTicketPrefix = (value = '') =>
  String(value || '')
    .toUpperCase()
    .replaceAll(/[^A-Z0-9]/g, '')
    .slice(0, 10);

const normalizeDomains = (domains = []) =>
  Array.from(
    new Set(
      (Array.isArray(domains) ? domains : [])
        .map((d) => String(d || '').trim().toLowerCase())
        .filter(Boolean)
    )
  );

const SuperAdminTenantForm = () => {
  const navigate = useNavigate();
  const { tenantId } = useParams();
  const isEditMode = Boolean(tenantId);

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [error, setError] = useState('');
  const [tenant, setTenant] = useState(null);
  const [formData, setFormData] = useState(emptyTenantForm);
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [pendingLogoFile, setPendingLogoFile] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState('');
  const [domainsText, setDomainsText] = useState('');
  const [activityLogs, setActivityLogs] = useState([]);

  useEffect(() => {
    return () => {
      if (logoPreviewUrl) {
        URL.revokeObjectURL(logoPreviewUrl);
      }
    };
  }, [logoPreviewUrl]);

  useEffect(() => {
    setActivityLogs(getAdminActivityLogs());
  }, []);

  useEffect(() => {
    if (!isEditMode) return;

    const fetchTenant = async () => {
      try {
        setLoading(true);
        let data = null;
        try {
          const res = await api.get(`/tenants/${tenantId}`);
          data = res.data?.data;
        } catch (singleErr) {
          // Fallback for environments where /tenants/:id returns 404 from stale backend process.
          if (singleErr?.response?.status === 404) {
            const listRes = await api.get('/tenants');
            const list = Array.isArray(listRes.data?.data) ? listRes.data.data : [];
            data = list.find((t) => String(t.id) === String(tenantId)) || null;
          } else {
            throw singleErr;
          }
        }
        if (!data) throw new Error('Tenant not found');

        setTenant(data);
        setFormData({
          name: data.name || '',
          slug: data.slug || '',
          ticket_prefix: data.ticket_prefix || suggestTicketPrefix(data.name || '', data.slug || ''),
          adminEmail: '',
          password: '',
          adminName: '',
          support_email: data.support_email || '',
          logo_url: data.logo_url || '',
          primary_color: data.primary_color || '#4f46e5',
          accent_color: data.accent_color || '#a855f7',
          portal_name: data.portal_name || '',
          is_active: !!data.is_active,
        });
        setDomainsText(
          normalizeDomains((data?.allowed_domains || []).map((d) => d?.domain || d)).join('\n')
        );
        const logs = appendAdminActivityLog({
          level: 'info',
          action: 'tenant.fetch.success',
          message: `Loaded tenant "${data.name}"`,
          context: { tenantId: data.id },
        });
        setActivityLogs(logs);
      } catch (err) {
        const logs = appendAdminActivityLog({
          level: 'error',
          action: 'tenant.fetch.error',
          message: err.response?.data?.message || err.message || 'Failed to load tenant',
          context: { tenantId },
        });
        setActivityLogs(logs);
        toast.error(err.response?.data?.message || err.message || 'Failed to load tenant');
        navigate('/super-admin/tenants');
      } finally {
        setLoading(false);
      }
    };

    fetchTenant();
  }, [isEditMode, navigate, tenantId]);

  const pageTitle = useMemo(
    () => (isEditMode ? 'Edit Tenant' : 'Create Tenant'),
    [isEditMode]
  );
  const pageDescription = 'Configure identification, branding, and administrators.';
  let submitLabel = 'Create Tenant & Admin';
  if (saving) {
    submitLabel = 'Processing...';
  } else if (isEditMode) {
    submitLabel = 'Update Settings';
  }

  const handleCompanyNameChange = (value) => {
    const nextName = value;
    const nextSlug = slugify(nextName);
    const nextPrefix = suggestTicketPrefix(nextName, nextSlug);
    const existingPortalName = `${formData.name} Helpdesk`;
    const existingAdminName = `${formData.name} Admin`;
    const nextPortalName = nextName ? `${nextName} Helpdesk` : '';
    const nextAdminName = nextName ? `${nextName} Admin` : '';
    setFormData((prev) => ({
      ...prev,
      name: nextName,
      slug: prev.slug && prev.slug !== slugify(prev.name) ? prev.slug : nextSlug,
      ticket_prefix:
        prev.ticket_prefix && prev.ticket_prefix !== suggestTicketPrefix(prev.name, slugify(prev.name))
          ? prev.ticket_prefix
          : nextPrefix,
      portal_name: prev.portal_name && prev.portal_name !== existingPortalName ? prev.portal_name : nextPortalName,
      adminName: prev.adminName && prev.adminName !== existingAdminName ? prev.adminName : nextAdminName,
    }));
  };

  const handleLogoFileUpload = async (file, tenantIdForUpload) => {
    const targetTenantId = tenantIdForUpload || tenant?.id;
    if (!targetTenantId) {
      toast.error('Create the tenant first, then upload its logo.');
      return;
    }
    if (!file) return;

    try {
      setUploadingLogo(true);
      const payload = new FormData();
      payload.append('logo', file);
      const res = await api.post(`/tenants/${targetTenantId}/logo`, payload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      const nextLogoUrl = res.data?.data?.logo_url || '';
      if (logoPreviewUrl) {
        URL.revokeObjectURL(logoPreviewUrl);
        setLogoPreviewUrl('');
      }
      setFormData((prev) => ({ ...prev, logo_url: nextLogoUrl }));
      setTenant((prev) => (prev ? { ...prev, logo_url: nextLogoUrl } : prev));
      const logs = appendAdminActivityLog({
        level: 'info',
        action: 'tenant.logo.upload.success',
        message: 'Tenant logo uploaded',
        context: { tenantId: targetTenantId },
      });
      setActivityLogs(logs);
      toast.success('Logo uploaded successfully');
    } catch (err) {
      const logs = appendAdminActivityLog({
        level: 'error',
        action: 'tenant.logo.upload.error',
        message: err.response?.data?.message || err.message || 'Failed to upload logo',
        context: { tenantId: targetTenantId },
      });
      setActivityLogs(logs);
      toast.error(err.response?.data?.message || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      setSaving(true);

      if (isEditMode) {
        const domains = normalizeDomains(domainsText.split(/[\n,]/g));
        await api.put(`/tenants/${tenantId}`, {
          name: formData.name,
          slug: formData.slug,
          ticket_prefix: formData.ticket_prefix,
          portal_name: formData.portal_name,
          support_email: formData.support_email || null,
          logo_url: formData.logo_url || null,
          primary_color: formData.primary_color,
          accent_color: formData.accent_color,
          is_active: !!formData.is_active,
          domains,
        });
        const logs = appendAdminActivityLog({
          level: 'info',
          action: 'tenant.update.success',
          message: `Updated tenant "${formData.name}"`,
          context: { tenantId },
        });
        setActivityLogs(logs);
        toast.success('Tenant updated successfully');
      } else {
        const createRes = await api.post('/tenants', {
          name: formData.name,
          slug: formData.slug,
          ticket_prefix: formData.ticket_prefix,
          portal_name: formData.portal_name,
          support_email: formData.support_email,
          logo_url: formData.logo_url,
          primary_color: formData.primary_color,
          accent_color: formData.accent_color,
          is_active: formData.is_active,
          domains: [],
          admin_name: formData.adminName,
          admin_email: formData.adminEmail,
          admin_password: formData.password,
        });
        const createdTenantId = createRes?.data?.data?.id;
        if (pendingLogoFile && createdTenantId) {
          await handleLogoFileUpload(pendingLogoFile, createdTenantId);
        }
        const logs = appendAdminActivityLog({
          level: 'info',
          action: 'tenant.create.success',
          message: `Created tenant "${formData.name}"`,
          context: { tenantId: createdTenantId || null },
        });
        setActivityLogs(logs);
        toast.success('Tenant created successfully');
      }

      navigate('/super-admin/tenants');
    } catch (err) {
      const logs = appendAdminActivityLog({
        level: 'error',
        action: isEditMode ? 'tenant.update.error' : 'tenant.create.error',
        message: err.response?.data?.message || err.message || 'Tenant save failed',
        context: { tenantId: tenantId || null },
      });
      setActivityLogs(logs);
      setError(err.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} tenant`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout userRole="super_admin">
        <div className="flex flex-col flex-1 min-h-0 w-full gap-4 bg-slate-50 dark:bg-slate-900">
          <div className="shrink-0 rounded-2xl tenant-brand-gradient text-white p-5 shadow-lg animate-pulse">
            <div className="h-8 w-48 rounded-lg bg-white/20" />
          </div>
          <div className="flex-1 min-h-0 flex items-center justify-center rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            <p className="text-sm text-slate-500 dark:text-slate-400">Loading tenant...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const inputClass =
    'w-full text-sm px-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800/50 text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-slate-400';
  const labelClass =
    'block text-[11px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5 ml-1';

  return (
    <DashboardLayout userRole="super_admin">
      <div className="flex flex-col flex-1 min-h-0 w-full gap-4 bg-slate-50 dark:bg-slate-900">
        <div className="shrink-0 rounded-2xl tenant-brand-gradient text-white p-4 sm:p-5 shadow-lg">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => navigate('/super-admin/tenants')}
              className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center hover:bg-white/30 transition-all active:scale-95"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <h1 className="text-xl font-bold tracking-tight">{pageTitle}</h1>
              <p className="text-white/80 text-xs sm:text-sm">{pageDescription}</p>
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain scrollbar-hide">
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden w-full">
            <form onSubmit={handleSubmit} className="p-4 sm:p-6 lg:p-8 space-y-6">
              {error && (
                <div className="p-4 bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 rounded-xl text-xs font-bold border border-red-100 dark:border-red-500/20">
                  {error}
                </div>
              )}

              <div className="space-y-5">
                <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                  <Building className="w-4 h-4 text-indigo-500 shrink-0" /> Identity & branding
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-5">
                  <div>
                    <label htmlFor="tenant-name" className={labelClass}>Company Name</label>
                    <input
                      id="tenant-name"
                      value={formData.name}
                      onChange={(e) => handleCompanyNameChange(e.target.value)}
                      required
                      placeholder="e.g. Acme Corp"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="tenant-slug" className={labelClass}>URL Slug</label>
                    <input
                      id="tenant-slug"
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: slugify(e.target.value) })}
                      required
                      placeholder="acme-corp"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="tenant-ticket-prefix" className={labelClass}>Ticket Prefix</label>
                    <input
                      id="tenant-ticket-prefix"
                      value={formData.ticket_prefix}
                      onChange={(e) => setFormData({ ...formData, ticket_prefix: sanitizeTicketPrefix(e.target.value) })}
                      required
                      placeholder="CAN"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="tenant-portal-name" className={labelClass}>Portal Display Name</label>
                    <input
                      id="tenant-portal-name"
                      value={formData.portal_name}
                      onChange={(e) => setFormData({ ...formData, portal_name: e.target.value })}
                      placeholder="Acme Support Portal"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="tenant-support-email" className={labelClass}>Support Email</label>
                    <input
                      id="tenant-support-email"
                      type="email"
                      value={formData.support_email}
                      onChange={(e) => setFormData({ ...formData, support_email: e.target.value })}
                      placeholder="support@acme.com"
                      className={inputClass}
                    />
                  </div>
                  {isEditMode && (
                    <div className="sm:col-span-2 xl:col-span-4">
                      <label htmlFor="tenant-domains" className={labelClass}>Allowed Domains</label>
                      <textarea
                        id="tenant-domains"
                        value={domainsText}
                        onChange={(e) => setDomainsText(e.target.value)}
                        placeholder={'acme.com\nsupport.acme.com'}
                        rows={4}
                        className={inputClass}
                      />
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        Add one domain per line (or comma separated). These domains will be saved to this tenant.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                <div className="md:col-span-2 xl:col-span-3 bg-slate-50 dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-700/50">
                  <label htmlFor="tenant-logo-url" className={labelClass}>Company Logo</label>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center justify-center overflow-hidden">
                      {logoPreviewUrl || formData.logo_url ? (
                        <img src={resolveMediaUrl(logoPreviewUrl || formData.logo_url)} alt="Tenant logo preview" className="w-full h-full object-contain" />
                      ) : (
                        <Upload className="text-slate-300" />
                      )}
                    </div>
                    <div className="flex-1 space-y-2">
                      <input
                        id="tenant-logo-url"
                        value={formData.logo_url}
                        onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                        placeholder="Paste Logo URL..."
                        className={inputClass}
                      />
                      <label htmlFor="tenant-logo-file" className="inline-flex items-center gap-2 text-[11px] font-bold text-indigo-500 cursor-pointer hover:underline">
                        <Upload className="w-3 h-3" />
                        {isEditMode ? 'Upload File Instead' : 'Upload File'}
                        <input
                          id="tenant-logo-file"
                          type="file"
                          accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                          className="hidden"
                          disabled={uploadingLogo}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            if (isEditMode) {
                              handleLogoFileUpload(file);
                            } else {
                              if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
                              setPendingLogoFile(file);
                              setLogoPreviewUrl(URL.createObjectURL(file));
                              toast.success('Logo selected. It will upload after tenant is created.');
                            }
                            e.target.value = '';
                          }}
                        />
                      </label>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 sm:gap-5 md:col-span-1 xl:col-span-1">
                  <div>
                    <label htmlFor="tenant-primary-color" className={labelClass}>Primary Color</label>
                    <div className="flex items-center gap-2 px-3 py-1.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                      <input
                        id="tenant-primary-color"
                        type="color"
                        value={formData.primary_color}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        className="w-8 h-8 rounded cursor-pointer bg-transparent"
                      />
                      <span className="text-xs font-mono text-slate-500 uppercase">{formData.primary_color}</span>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="tenant-accent-color" className={labelClass}>Accent Color</label>
                    <div className="flex items-center gap-2 px-3 py-1.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                      <input
                        id="tenant-accent-color"
                        type="color"
                        value={formData.accent_color}
                        onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                        className="w-8 h-8 rounded cursor-pointer bg-transparent"
                      />
                      <span className="text-xs font-mono text-slate-500 uppercase">{formData.accent_color}</span>
                    </div>
                  </div>
                </div>

                {isEditMode && (
                  <div className="md:col-span-2 xl:col-span-2">
                    <label htmlFor="tenant-status" className={labelClass}>Status</label>
                    <select
                      id="tenant-status"
                      value={formData.is_active ? 'active' : 'inactive'}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
                      className={inputClass}
                    >
                      <option value="active">Active (Full Access)</option>
                      <option value="inactive">Inactive (Suspended)</option>
                    </select>
                  </div>
                )}
              </div>

              {!isEditMode && (
                <div className="pt-4 space-y-4 border-t border-slate-100 dark:border-slate-700">
                  <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-emerald-500" /> Initial Administrator
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <input
                      value={formData.adminName}
                      onChange={(e) => setFormData({ ...formData, adminName: e.target.value })}
                      required
                      placeholder="Admin Full Name"
                      className={inputClass}
                    />
                    <input
                      type="email"
                      value={formData.adminEmail}
                      onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                      required
                      placeholder="Admin Email"
                      className={inputClass}
                    />
                    <div className="md:col-span-2">
                      <label htmlFor="tenant-admin-password" className={labelClass}>
                        Temporary password
                      </label>
                      <div className="relative">
                        <input
                          id="tenant-admin-password"
                          type={showAdminPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          required
                          autoComplete="new-password"
                          placeholder="Temporary Password"
                          className={`${inputClass} pr-12`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowAdminPassword((v) => !v)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 dark:hover:text-slate-200 transition-colors"
                          aria-label={showAdminPassword ? 'Hide password' : 'Show password'}
                        >
                          {showAdminPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="pt-6 flex gap-3 border-t border-slate-100 dark:border-slate-700">
                <button
                  type="button"
                  onClick={() => navigate('/super-admin/tenants')}
                  className="flex-1 px-6 py-3 text-sm font-bold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-[2] inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-bold text-white tenant-brand-gradient rounded-xl shadow-lg shadow-indigo-500/20 hover:opacity-90 transition-all active:scale-[0.98] disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {submitLabel}
                </button>
              </div>
            </form>
          </div>
          <aside className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-4 h-fit xl:sticky xl:top-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                <Activity className="w-4 h-4 text-indigo-500" />
                Activity Logs
              </h3>
              <span className="text-xs text-slate-500">{activityLogs.length}</span>
            </div>
            <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
              {activityLogs.length === 0 ? (
                <p className="text-xs text-slate-500">No activity logs yet.</p>
              ) : (
                activityLogs.slice(0, 30).map((log) => (
                  <div
                    key={log.id}
                    className={`rounded-xl border px-3 py-2 ${
                      log.level === 'error'
                        ? 'border-red-200 bg-red-50 dark:border-red-500/20 dark:bg-red-500/10'
                        : log.level === 'warn'
                        ? 'border-amber-200 bg-amber-50 dark:border-amber-500/20 dark:bg-amber-500/10'
                        : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/40'
                    }`}
                  >
                    <p className="text-[11px] font-bold text-slate-700 dark:text-slate-200 flex items-center gap-1">
                      {log.level === 'error' ? <AlertTriangle className="w-3.5 h-3.5 text-red-500" /> : null}
                      {log.action}
                    </p>
                    <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">{log.message}</p>
                    <p className="text-[10px] text-slate-400 mt-1">{new Date(log.createdAt).toLocaleString()}</p>
                  </div>
                ))
              )}
            </div>
          </aside>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SuperAdminTenantForm;
